import { cn } from "@/lib/utils.ts";
import { LocationSearch } from "@/components/location/LocationSearch.tsx";
import {
  matchedCoordinates,
  type SavedAddressCoordinates,
  type SavedAddressType,
  useSavedAddresses,
} from "@/components/location/useSavedAddresses.ts";
import { useUpdateSurfacePreferences } from "@/components/preferences/usePreferences.tsx";
import { Button } from "@/components/ui/button.tsx";
import { errorToast } from "@/components/ui/toast.tsx";
import { useCurrentUserContext } from "@/hooks/useUser.tsx";
import { type LocationSuggestion } from "@/lib/places.ts";
import { useQueryClient } from "@tanstack/react-query";
import { Building2, Check, Home, MonitorSmartphone } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { panelDescriptionClass, SettingsPanelHeader } from "./SettingsPanelHeader.tsx";

interface LocationPreferencesProps {
  timezonePreference: string;
  isUpdating?: boolean;
}

/** 36px gray circle wrapping a 16px icon — shared by Saved Addresses and Timezone rows. */
const CircleIcon = ({ children }: { children: React.ReactNode }) => (
  <div className="shrink-0 size-9 rounded-full bg-zinc-100 flex items-center justify-center text-zinc-500 [&>svg]:w-4 [&>svg]:h-4">
    {children}
  </div>
);

/**
 * Editable location row for a single location type (home, work, etc.) — rendered
 * as a row inside the Saved Addresses grouped card. One always-editable field
 * in the picker card's grammar: icon inside on the left, the label stacked
 * over the input line, the remove control at the far edge. Save/Cancel appear
 * under the field only once the draft differs from what's saved.
 */
const LocationRow = ({
  label,
  icon,
  locationType,
  locationValue,
  isUpdating,
  onSave,
  onDelete,
}: {
  label: string;
  icon: React.ReactNode;
  locationType: SavedAddressType;
  locationValue: string;
  isUpdating: boolean;
  onSave: (address: string, coordinates?: SavedAddressCoordinates) => Promise<boolean>;
  onDelete: () => Promise<boolean>;
}) => {
  // Null means "reading through to the saved value": a background refetch
  // updates the field until the user starts typing, and never after.
  const [draft, setDraft] = useState<string | null>(null);
  const [pendingLocation, setPendingLocation] = useState<LocationSuggestion | null>(null);

  const value = draft ?? locationValue;
  const trimmed = value.trim();
  // An emptied field is not a delete; the field's own remove control is.
  const canSave = trimmed.length > 0 && trimmed !== locationValue;

  const resetToSaved = () => {
    setDraft(null);
    setPendingLocation(null);
  };

  const handleLocationSelect = (location: LocationSuggestion) => {
    setDraft(location.address || location.name);
    setPendingLocation(location);
  };

  const handleSave = async () => {
    if (!canSave) return;
    if (await onSave(trimmed, matchedCoordinates(pendingLocation, trimmed))) resetToSaved();
  };

  return (
    <div>
      {/* One field carries the whole row, in the picker card's grammar: the
          type's icon inside on the left, the label stacked over the input,
          and the remove control at the far edge. Always editable — there is
          no view mode to flip in and out of. */}
      <LocationSearch
        value={value}
        onChange={setDraft}
        onLocationSelect={handleLocationSelect}
        placeholder={`Search for a ${label.toLowerCase()} address`}
        disabled={isUpdating}
        showCurrentLocation
        showDropdown
        icon={<span className="text-muted-foreground [&>svg]:size-4">{icon}</span>}
        label={label}
        onDelete={
          locationValue
            ? () =>
                void onDelete().then((removed) => {
                  if (removed) resetToSaved();
                })
            : undefined
        }
        className="rounded-none bg-transparent"
      />
      {canSave && (
        // Inset to the field's own padding: the group has none of its own, so
        // without this the buttons sit against its edge.
        <div className="mt-2 flex items-center justify-end gap-4 px-2.5 pb-2.5">
          <Button
            type="button"
            variant="link"
            size="sm"
            onClick={resetToSaved}
            disabled={isUpdating}
            className="text-foreground underline hover:no-underline"
            data-statsig-name={`settings-${locationType}-location-cancel`}
          >
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={handleSave}
            disabled={isUpdating}
            data-statsig-name={`settings-${locationType}-location-save`}
          >
            {isUpdating ? "Saving..." : "Save"}
          </Button>
        </div>
      )}
    </div>
  );
};

/**
 * A single timezone option rendered as a row inside the Timezone grouped card.
 * The whole row is clickable; the selected row shows a check on the right.
 */
const TimezoneRow = ({
  label,
  description,
  icon,
  checked,
  disabled,
  onSelect,
  statsigName,
}: {
  label: string;
  description: string;
  icon: React.ReactNode;
  checked: boolean;
  disabled: boolean;
  onSelect: () => void;
  statsigName: string;
}) => (
  <Button
    type="button"
    variant="blank"
    size="fit"
    role="radio"
    aria-checked={checked}
    // Roving tabindex: only the selected radio is in the Tab order; arrow keys
    // (handled on the radiogroup) move between the rest.
    tabIndex={checked ? 0 : -1}
    onClick={onSelect}
    disabled={disabled}
    className="w-full flex items-center gap-3 px-4 py-3 text-left whitespace-normal rounded-none hover:bg-secondary disabled:opacity-70"
    data-statsig-name={statsigName}
  >
    <CircleIcon>{icon}</CircleIcon>
    <div className="flex-1 min-w-0">
      <div className="text-sm font-medium">{label}</div>
      <div className="text-sm text-muted-foreground">{description}</div>
    </div>
    {checked && <Check className="size-4 shrink-0 text-primary" />}
  </Button>
);

/**
 * The user's home and work addresses, as an editable grouped card.
 *
 * Owns its own data — it reads the addresses off the user context and
 * invalidates what depends on them — so any surface that wants this pair can
 * render it with no props. The picker card does exactly that.
 *
 * Each row commits on its own Save: there is no card-level save gating the
 * pair, so filling in home and walking away keeps home.
 */
export const SavedAddresses = ({
  isUpdating = false,
  className,
}: {
  isUpdating?: boolean;
  /**
   * The group's own chrome, which is the one thing that differs by where this
   * renders. Settings wears the timezone group's treatment, since the two sit
   * one above the other on that page; the canvas card overrides it to the omni
   * card's, where a card-coloured group would have no edge at all against the
   * modal behind it.
   */
  className?: string;
}) => {
  const { addresses, isSaving, save, remove } = useSavedAddresses();

  return (
    // One grouped table: the group owns the edge and the background, the rows
    // are flush inside it, and a hairline is all that separates them. No
    // `overflow-hidden` to hold the rounding, which is what the timezone group
    // below does too: the rows paint nothing of their own, so there is nothing
    // to clip. Clipping here would also swallow the address typeahead, which
    // hangs below its field and past the group on the last row.
    <div className={cn("flex flex-col rounded-2xl bg-card", className)}>
      <LocationRow
        label="Home"
        icon={<Home />}
        locationType="home"
        locationValue={addresses.home}
        isUpdating={isUpdating || isSaving("home")}
        onSave={(address, coordinates) => save("home", address, coordinates)}
        onDelete={() => remove("home")}
      />
      <div className="border-t-[0.5px] border-border" />
      <LocationRow
        label="Work"
        icon={<Building2 />}
        locationType="work"
        locationValue={addresses.work}
        isUpdating={isUpdating || isSaving("work")}
        onSave={(address, coordinates) => save("work", address, coordinates)}
        onDelete={() => remove("work")}
      />
    </div>
  );
};

/**
 * Location preferences component for user settings.
 * Shows saved Home/Work addresses and a timezone preference picker.
 */
export const LocationPreferences = ({
  timezonePreference,
  isUpdating: externalIsUpdating = false,
}: LocationPreferencesProps) => {
  const { userContext } = useCurrentUserContext();
  const updateLocationPreferences = useUpdateSurfacePreferences("location");
  const queryClient = useQueryClient();
  const [internalIsUpdating, setInternalIsUpdating] = useState(false);
  const [pendingTimezonePreference, setPendingTimezonePreference] =
    useState<string>(timezonePreference);

  const locations = userContext?.user.userContact.locations ?? [];
  const homeLocation = locations.find((l) => l.type === "home");
  const homeLocationName = homeLocation?.value || "";

  // Keep pending timezone in sync when the upstream preference changes.
  useEffect(() => {
    setPendingTimezonePreference(timezonePreference);
  }, [timezonePreference]);

  const isUpdating = externalIsUpdating || internalIsUpdating;

  /**
   * Invalidate caches that depend on timezone preferences
   */
  const invalidateTimezonePreferenceCaches = useCallback(async () => {
    // Agenda and calendar grid render event times in the effective timezone and
    // live under separate query keys — refetch both so neither keeps showing the
    // old timezone's times after the preference changes.
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["dailyAgenda"] }),
      queryClient.invalidateQueries({ queryKey: ["calendarEvents"] }),
    ]);
  }, [queryClient]);

  // Immediate-save on row click: optimistically update the UI, write to the
  // backend, and roll back the optimistic state on failure.
  const handleSelectTimezone = useCallback(
    async (preference: string) => {
      if (preference === pendingTimezonePreference) return;
      const previous = pendingTimezonePreference;
      setPendingTimezonePreference(preference);
      setInternalIsUpdating(true);
      try {
        await updateLocationPreferences({ timezonePreference: preference });
        await invalidateTimezonePreferenceCaches();
      } catch (_error) {
        setPendingTimezonePreference(previous);
        errorToast("Failed to update timezone preference. Please try again.");
      } finally {
        setInternalIsUpdating(false);
      }
    },
    [pendingTimezonePreference, updateLocationPreferences, invalidateTimezonePreferenceCaches],
  );

  // WAI-ARIA radiogroup keyboard model: arrow keys move focus to the adjacent
  // enabled radio and select it (selection-follows-focus). Tab order itself is
  // handled by the roving tabIndex on each TimezoneRow.
  const handleTimezoneKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!["ArrowDown", "ArrowRight", "ArrowUp", "ArrowLeft"].includes(e.key)) return;
    e.preventDefault();
    const radios = Array.from(
      e.currentTarget.querySelectorAll<HTMLButtonElement>('[role="radio"]:not(:disabled)'),
    );
    if (radios.length === 0) return;
    const currentIndex = radios.indexOf(document.activeElement as HTMLButtonElement);
    const forward = e.key === "ArrowDown" || e.key === "ArrowRight";
    const nextIndex =
      currentIndex === -1 ? 0 : (currentIndex + (forward ? 1 : -1) + radios.length) % radios.length;
    const next = radios[nextIndex];
    next.focus();
    next.click();
  }, []);

  const formatTimezoneDescription = useCallback((timezoneId: string): string => {
    try {
      const now = new Date();

      const formatter = new Intl.DateTimeFormat("en-US", {
        timeZone: timezoneId,
        timeZoneName: "longOffset",
      });
      const parts = formatter.formatToParts(now);
      const offsetPart = parts.find((part) => part.type === "timeZoneName")?.value;

      const longNameFormatter = new Intl.DateTimeFormat("en-US", {
        timeZone: timezoneId,
        timeZoneName: "long",
      });
      const longParts = longNameFormatter.formatToParts(now);
      const longName = longParts.find((part) => part.type === "timeZoneName")?.value;

      const shortNameFormatter = new Intl.DateTimeFormat("en-US", {
        timeZone: timezoneId,
        timeZoneName: "short",
      });
      const shortParts = shortNameFormatter.formatToParts(now);
      const shortName = shortParts.find((part) => part.type === "timeZoneName")?.value;

      if (offsetPart && longName && shortName) {
        return `${offsetPart} - ${longName} (${shortName})`;
      }

      return timezoneId;
    } catch (error) {
      console.error("Error formatting timezone:", error);
      return timezoneId;
    }
  }, []);

  const homeLocationDescription = useMemo(() => {
    if (!homeLocation) return "You need to set a home location to use this preference";

    if (homeLocation.timezoneId) {
      return formatTimezoneDescription(homeLocation.timezoneId);
    }

    return homeLocationName;
  }, [homeLocation, homeLocationName, formatTimezoneDescription]);

  return (
    <>
      {/* Header outside the space-y root so the gap under the title stays the
          standard mb-6 instead of the section rhythm. */}
      <SettingsPanelHeader title="Locations" />
      <p className={panelDescriptionClass}>
        Your saved places, used for travel time and location-aware scheduling.
      </p>
      <div className="w-full space-y-8 pb-12">
        <div>
          <h2 className="text-base font-semibold mb-2">Saved addresses</h2>
          <SavedAddresses isUpdating={isUpdating} />
        </div>

        <div>
          <h2 className="text-base font-semibold mb-2">Timezone</h2>
          <p className="text-sm text-muted-foreground mb-4">
            Choose which timezone to use for scheduling if you are away from your home location
          </p>
          <div
            role="radiogroup"
            aria-label="Timezone preference"
            onKeyDown={handleTimezoneKeyDown}
            className="rounded-2xl bg-card"
          >
            <TimezoneRow
              label="Current Device"
              description={formatTimezoneDescription(
                Intl.DateTimeFormat().resolvedOptions().timeZone,
              )}
              icon={<MonitorSmartphone />}
              checked={pendingTimezonePreference === "current"}
              disabled={isUpdating}
              onSelect={() => handleSelectTimezone("current")}
              statsigName="settings-location-timezone-current"
            />
            <div className="border-t-[0.5px] border-border" />
            <TimezoneRow
              label="Home Location"
              description={homeLocationDescription}
              icon={<Home />}
              checked={pendingTimezonePreference === "home"}
              disabled={isUpdating || !homeLocationName}
              onSelect={() => handleSelectTimezone("home")}
              statsigName="settings-location-timezone-home"
            />
          </div>
        </div>
      </div>
    </>
  );
};
